import type { AgentCursor } from "@/stores/canvas-store";

/**
 * desktop_interact, and the names it replaced that an older desktop companion
 * still sends until it updates (the web app shows its runs). Read only: no
 * agent is offered these names. The backend labels an old call as the
 * desktop_interact call it means, so its step reads the same either way. The
 * read tools look replaced are not here: a call that only reads never takes
 * over the cursor.
 */
const DESKTOP_INTERACTION_NAMES = [
  "interact",
  "act",
  "guide",
  "open_app",
  "open_url",
  "open_path",
  "reveal_path",
  "activate_browser_tab",
  "focus_window",
  "move_window_to_screen",
  "arrange_window",
  "set_window_frame",
  "media_control",
  "media_status",
];
const DESKTOP_INTERACTION_TOOL = new RegExp(
  `(?:^|__|\\.)desktop_(?:${DESKTOP_INTERACTION_NAMES.join("|")})$`,
);

/** A desktop call that acts, guides, opens, places or plays. One that only
 *  reads (the backend marks it `reads_only`) leaves the reply on the cursor. */
export function isDesktopInteractionTool(
  name: string | undefined,
  readsOnly?: boolean,
): boolean {
  return !!name && !readsOnly && DESKTOP_INTERACTION_TOOL.test(name);
}

/** Keep display ownership through reply updates, but never across turns. */
export function retainDesktopInteractionOwnership(
  merged: AgentCursor,
  incoming: Partial<AgentCursor>,
  previous?: AgentCursor,
): void {
  // readsOnly describes the tool it arrived with, so it travels with toolName.
  const sameRun = previous?.executionId === merged.executionId;
  const incomingTool = !!incoming.toolCall || incoming.toolName !== undefined;
  merged.toolName = incomingTool
    ? incoming.toolName
    : sameRun
      ? previous?.toolName
      : undefined;
  merged.readsOnly = incomingTool
    ? incoming.readsOnly
    : sameRun
      ? previous?.readsOnly
      : undefined;
  merged.desktopInteractionExecutionId = isDesktopInteractionTool(
    incoming.toolName,
    incoming.readsOnly,
  )
    ? merged.executionId
    : previous?.desktopInteractionExecutionId === merged.executionId
      ? previous?.desktopInteractionExecutionId
      : undefined;
  // A finished run's last step is over; the tray card has its result.
  const desktopOwned =
    !!merged.desktopInteractionExecutionId &&
    merged.desktopInteractionExecutionId === previous?.executionId;
  if (!desktopOwned || incoming.toolCall) return;
  if (merged.frozen) {
    merged.toolCall = undefined;
    merged.toolCallLabel = undefined;
    merged.toolFailed = undefined;
  }
}
